<script>
import WebLayout from '@/layouts/Web/Layout.vue'

export default {
    // Using shorthand syntax...
    layout: WebLayout,
}
</script>

<script setup>
import SEOHead from '@/components/SEOHead.vue'
import { ref, onMounted } from 'vue';

const showCookieSettings = ref(false);
const cookiePreferences = ref({
    necessary: true, // Always true, cannot be disabled
    functional: false,
    analytics: false,
    marketing: false
});

const cookieCategories = {
    necessary: {
        name: 'Nujni piškotki',
        description: 'Ti piškotki so potrebni za osnovno delovanje spletne strani in jih ni mogoče onemogočiti.',
        required: true
    },
    functional: {
        name: 'Funkcionalni piškotki',
        description: 'Ti piškotki omogočajo izboljšano funkcionalnost in personalizacijo.',
        required: false
    },
    analytics: {
        name: 'Analitični piškotki',
        description: 'Ti piškotki nam pomagajo razumeti, kako obiskovalci uporabljajo spletno stran.',
        required: false
    },
    marketing: {
        name: 'Trženjski piškotki',
        description: 'Ti piškotki se uporabljajo za prikazovanje oglasov, ki so relevantni za vas.',
        required: false
    }
};

onMounted(() => {
    // Load saved preferences
    const savedPreferences = localStorage.getItem('cookiePreferences');
    if (savedPreferences) {
        cookiePreferences.value = { ...cookiePreferences.value, ...JSON.parse(savedPreferences) };
    }
});

const openCookieSettings = () => {
    showCookieSettings.value = true;
};

const closeCookieSettings = () => {
    showCookieSettings.value = false;
};

const toggleCategory = (category) => {
    if (!cookieCategories[category].required) {
        cookiePreferences.value[category] = !cookiePreferences.value[category];
    }
};

const savePreferences = () => {
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences.value));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    
    // Trigger custom event for other components to listen
    window.dispatchEvent(new CustomEvent('cookiePreferencesUpdated', {
        detail: cookiePreferences.value
    }));
    
    showCookieSettings.value = false;
};
</script>

<template>
    <SEOHead
        title="Politika piškotkov - Kulfood"
        description="Politika piškotkov Kulfood. Pojasnilo o vrstah piškotkov, njihovi uporabi in možnostih upravljanja vaših nastavitev."
        keywords="piškotki, politika piškotkov, Kulfood.si"
        canonical="/politika-piskotkov"
        image="/images/web/kulfood-cookie-policy.png"
        :structured-data="{
            '@type': 'WebSite',
            'name': 'Kulfood.si',
            'url': 'https://kulfood.si',
            'description': 'Politika piškotkov Kulfood. Pojasnilo o vrstah piškotkov, njihovi uporabi in možnostih upravljanja vaših nastavitev.',
        }"
        type="website"
    />
    <div class="container mx-auto px-4 py-12">
        <div class="max-w-4xl mx-auto">
            <h1 class="text-4xl font-bold text-gray-900 mb-8">Politika piškotkov</h1>
            
            <div class="prose prose-lg max-w-none">
                <h2 class="text-2xl font-semibold text-gray-900 mt-8 mb-4">Politika piškotkov – Kulfood.si</h2>
                
                <p class="text-gray-600 mb-6">
                    Spletna stran Kulfood.si uporablja piškotke in podobne tehnologije za zagotavljanje delovanja, izboljšanje uporabniške izkušnje in anonimno analitiko. Spodaj pojasnjujemo, katere piškotke uporabljamo, zakaj ter kako lahko z njimi upravljate.
                </p>

                <h2 class="text-2xl font-semibold text-gray-900 mt-8 mb-4">1. Kaj so piškotki?</h2>
                
                <p class="text-gray-600 mb-6">
                    Piškotki so majhne besedilne datoteke, ki jih spletna stran ob obisku shrani v vašo napravo (računalnik, pametni telefon ipd.). Z njihovo pomočjo si stran lahko zapomni vaše nastavitve, omogoči osnovne funkcije in analizira obisk.
                </p>

                <h2 class="text-2xl font-semibold text-gray-900 mt-8 mb-4">2. Vrste piškotkov, ki jih uporabljamo</h2>
                
                <div class="overflow-x-auto mb-6">
                    <table class="min-w-full border border-gray-200 rounded-lg">
                        <thead class="bg-gray-50">
                            <tr>
                                <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200">Vrsta piškotka</th>
                                <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200">Namen</th>
                                <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200">Primeri</th>
                                <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200">Trajanje</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            <tr>
                                <td class="px-4 py-3 text-sm text-gray-900 font-medium">Nujni (obvezni)</td>
                                <td class="px-4 py-3 text-sm text-gray-600">Omogočajo osnovno delovanje spletne strani (npr. prijava, varnost sej).</td>
                                <td class="px-4 py-3 text-sm text-gray-600">Seja uporabnika, varnostni žeton</td>
                                <td class="px-4 py-3 text-sm text-gray-600">Do zaprtja brskalnika</td>
                            </tr>
                            <tr>
                                <td class="px-4 py-3 text-sm text-gray-900 font-medium">Analitični</td>
                                <td class="px-4 py-3 text-sm text-gray-600">Pomagajo nam razumeti, kako obiskovalci uporabljajo spletno stran.</td>
                                <td class="px-4 py-3 text-sm text-gray-600">Google Analytics (anonimiziran)</td>
                                <td class="px-4 py-3 text-sm text-gray-600">1 dan – 2 leti</td>
                            </tr>
                            <tr>
                                <td class="px-4 py-3 text-sm text-gray-900 font-medium">Funkcionalni</td>
                                <td class="px-4 py-3 text-sm text-gray-600">Omogočajo shranjevanje izbranih nastavitev (npr. jezik).</td>
                                <td class="px-4 py-3 text-sm text-gray-600">Shrani izbiro jezika</td>
                                <td class="px-4 py-3 text-sm text-gray-600">Nekaj dni – mesecev</td>
                            </tr>
                            <tr>
                                <td class="px-4 py-3 text-sm text-gray-900 font-medium">Trženjski (če so prisotni)</td>
                                <td class="px-4 py-3 text-sm text-gray-600">Uporabljajo se za prikaz ciljanih oglasov preko zunanjih storitev.</td>
                                <td class="px-4 py-3 text-sm text-gray-600">Facebook Pixel ipd.</td>
                                <td class="px-4 py-3 text-sm text-gray-600">Do 2 let</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                
                <p class="text-gray-600 mb-6">
                    Trženjske piškotke bomo uporabili le, če boste za to podali izrecno soglasje.
                </p>

                <h2 class="text-2xl font-semibold text-gray-900 mt-8 mb-4">3. Upravljanje soglasja</h2>
                
                <p class="text-gray-600 mb-4">
                    Ob prvem obisku strani se vam prikaže pasica, kjer lahko:
                </p>
                
                <ul class="list-disc list-inside text-gray-600 mb-4 space-y-2">
                    <li>sprejmete vse piškotke,</li>
                    <li>prilagodite izbiro (npr. zavrnete trženjske ali analitične piškotke),</li>
                    <li>sprejmete le nujne piškotke.</li>
                </ul>
                
                <p class="text-gray-600 mb-6">
                    Svojo izbiro lahko kadarkoli spremenite s klikom na povezavo »Upravljanje piškotkov«, ki se nahaja v nogi spletne strani.
                </p>

                <h2 class="text-2xl font-semibold text-gray-900 mt-8 mb-4">4. Google Analytics</h2>
                
                <p class="text-gray-600 mb-4">
                    Za zbiranje podatkov o uporabi strani uporabljamo storitev Google Analytics, pri čemer:
                </p>
                
                <ul class="list-disc list-inside text-gray-600 mb-4 space-y-2">
                    <li>so IP naslovi obiskovalcev anonimizirani (skrajšani pred shranjevanjem),</li>
                    <li>se podatki ne povezujejo z vašimi osebnimi podatki,</li>
                    <li>se uporabljajo izključno za statistične namene.</li>
                </ul>
                
                <p class="text-gray-600 mb-4">
                    Če želite, lahko preprečite sledenje s strani Google Analytics na naslednji povezavi:
                </p>
                
                <p class="text-gray-600 mb-6">
                    <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">
                        https://tools.google.com/dlpage/gaoptout
                    </a>
                </p>

                <h2 class="text-2xl font-semibold text-gray-900 mt-8 mb-4">5. Upravljanje piškotkov v brskalniku</h2>
                
                <p class="text-gray-600 mb-4">
                    Piškotke lahko kadarkoli izbrišete ali onemogočite v nastavitvah svojega spletnega brskalnika. Upoštevajte, da lahko onemogočanje nekaterih piškotkov vpliva na delovanje spletne strani.
                </p>
                
                <p class="text-gray-600 mb-4">
                    Povezave do navodil za priljubljene brskalnike:
                </p>
                
                <ul class="list-disc list-inside text-gray-600 mb-6 space-y-2">
                    <li>Google Chrome</li>
                    <li>Mozilla Firefox</li>
                    <li>Apple Safari</li>
                </ul>

                <h2 class="text-2xl font-semibold text-gray-900 mt-8 mb-4">6. Spremembe politike</h2>
                
                <p class="text-gray-600 mb-6">
                    To Politiko piškotkov lahko občasno posodobimo zaradi zakonodajnih ali tehničnih sprememb. Vse spremembe bodo objavljene na tej strani. Priporočamo, da jo občasno ponovno preverite.
                </p>

                <h2 class="text-2xl font-semibold text-gray-900 mt-8 mb-4">7. Kontakt</h2>
                
                <p class="text-gray-600 mb-4">
                    Za dodatna vprašanja ali pomoč v zvezi z uporabo piškotkov nam lahko pišete na:
                </p>
                
                <p class="text-gray-600 mb-6">
                    <strong>info@kulfood.si</strong>
                </p>

                <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <h4 class="text-lg font-semibold text-blue-900 mb-2">Vaše možnosti</h4>
                    <p class="text-blue-800 mb-3">
                        Lahko kadarkoli spremenite svoje nastavitve piškotkov ali prekličete soglasje.
                    </p>
                    <button
                        @click="openCookieSettings"
                        class="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                    >
                        Spremeni nastavitve piškotkov
                    </button>
                </div>

                <p class="text-sm text-gray-500 mt-8">
                    Zadnja posodobitev: 30. 12. 2025
                </p>
            </div>
        </div>
    </div>

    <!-- Cookie Settings Modal -->
    <div v-if="showCookieSettings" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="flex min-h-screen items-center justify-center p-4">
            <div class="fixed inset-0 bg-black bg-opacity-50" @click="closeCookieSettings"></div>
            
            <div class="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div class="p-6">
                    <div class="flex justify-between items-center mb-6">
                        <h2 class="text-2xl font-bold text-gray-900">Nastavitve piškotkov</h2>
                        <button
                            @click="closeCookieSettings"
                            class="text-gray-400 hover:text-gray-600"
                        >
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                        </button>
                    </div>

                    <p class="text-gray-600 mb-6">
                        Spodaj lahko izberete, katere piškotke želite sprejeti. 
                        Nujni piškotki so potrebni za osnovno delovanje spletne strani.
                    </p>

                    <div class="space-y-4 mb-6">
                        <div v-for="(category, key) in cookieCategories" :key="key" 
                                class="border border-gray-200 rounded-lg p-4">
                            <div class="flex items-start justify-between">
                                <div class="flex-1">
                                    <div class="flex items-center gap-3">
                                        <h3 class="text-lg font-semibold text-gray-900">{{ category.name }}</h3>
                                        <span v-if="category.required" 
                                                class="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                                            Obvezno
                                        </span>
                                    </div>
                                    <p class="text-gray-600 text-sm mt-1">{{ category.description }}</p>
                                </div>
                                <div class="ml-4">
                                    <label class="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            :checked="cookiePreferences[key]"
                                            :disabled="category.required"
                                            @change="toggleCategory(key)"
                                            class="sr-only peer"
                                        >
                                        <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col sm:flex-row gap-3 justify-end">
                        <button
                            @click="closeCookieSettings"
                            class="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                        >
                            Prekliči
                        </button>
                        <button
                            @click="savePreferences"
                            class="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                        >
                            Shrani nastavitve
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>